import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight, Clock, FileText } from 'lucide-react';

const ICONS = {
  alert: AlertTriangle,
  document: FileText,
  clock: Clock,
};

const ICON_TONES = {
  orange: {
    shell: 'bg-orange-500/15 border-orange-400/30 text-orange-300 shadow-[0_0_20px_rgba(251,146,60,0.15)]',
  },
  blue: {
    shell: 'bg-blue-500/15 border-blue-400/30 text-blue-300 shadow-[0_0_20px_rgba(59,130,246,0.12)]',
  },
  purple: {
    shell: 'bg-violet-500/15 border-violet-400/30 text-violet-300 shadow-[0_0_20px_rgba(139,92,246,0.12)]',
  },
};

function UrgencyBar({ filled, tone = 'orange' }) {
  const activeClass =
    tone === 'orange'
      ? 'bg-orange-400'
      : 'bg-blue-400';

  return (
    <div className="hidden md:flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, index) => (
        <span
          key={index}
          className={`h-1.5 w-5 rounded-full ${
            index < filled ? activeClass : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

function InboxRow({ item, onTreat, treatLabel }) {
  const Icon = ICONS[item.icon] || AlertTriangle;
  const tone = ICON_TONES[item.iconTone] || ICON_TONES.orange;
  const barTone = item.priority === 'high' ? 'orange' : 'blue';

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-5 sm:px-6 py-4 border-b border-white/[0.06] last:border-b-0">
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <div
          className={`shrink-0 h-11 w-11 rounded-full border flex items-center justify-center ${tone.shell}`}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <p className="m-0 text-sm sm:text-[0.95rem] font-semibold text-white">{item.title}</p>
          <p className="m-0 mt-1 text-xs sm:text-sm text-white/45">{item.subtitle}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 sm:gap-5 shrink-0 pl-[3.75rem] sm:pl-0">
        <div className="hidden lg:flex items-center gap-4">
          <span className="text-sm text-white/40 w-16">{item.priorityLabel}</span>
          <UrgencyBar filled={item.urgencySegments} tone={barTone} />
        </div>

        <button
          type="button"
          onClick={() => onTreat?.(item)}
          className={
            item.primaryAction
              ? 'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-orange-500 hover:bg-orange-400 text-white transition-colors'
              : 'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors'
          }
        >
          {treatLabel}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function AIAgentSmartInbox({
  items = [],
  isLoading = false,
  onTreatItem,
  showWhenEmpty = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.smartInbox.${key}`, opts);

  if (isLoading) {
    return (
      <section className="mx-auto w-full max-w-4xl mb-4">
        <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden animate-pulse">
          <div className="px-5 sm:px-6 py-4 border-b border-white/10">
            <div className="h-5 w-44 bg-white/10 rounded-lg" />
          </div>
          <div className="px-5 sm:px-6 py-8 space-y-4">
            <div className="h-12 bg-white/5 rounded-2xl" />
            <div className="h-12 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (!items.length && !showWhenEmpty) return null;

  return (
    <section className="mx-auto w-full max-w-4xl mb-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/10">
          <h2 className="m-0 text-base sm:text-lg font-semibold text-white">{p('title')}</h2>
        </div>

        {items.length === 0 ? (
          <p className="m-0 px-5 sm:px-6 py-8 text-sm text-white/45 text-center">{p('empty')}</p>
        ) : (
          <div>
            {items.map((item) => (
              <InboxRow key={item.id} item={item} onTreat={onTreatItem} treatLabel={p('treat')} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
